import React, { useState } from 'react';
import Axios from 'axios';
import {
  Box,
  FormControl,
  InputLabel,
  Input,
  Button,
  ButtonGroup,
  Typography,
  Card,
  CardContent,
} from '@material-ui/core';

import AdminLayout from '../../../layouts/AdminLayout';
import { cleanCode } from '../../../libs/cleaner';
import useSolutionGallery from '../../../hooks/useSolutionGallery';
import useSpecComments from '../../../hooks/useSpecComments';
import Spec from '../../../models/Spec';
import { SpecTest } from '../../../components/Spec';
import utils from '../../../libs/utils';

interface Activity {
  id: string
  name: string
  answer: string
}

const Investigate = () => {
  const [activity, setActivity] = useState(null as Activity | null);
  const [spec, setSpec] = useState<Spec|null>(null);
  const [reference, setReference] = useState('' as string);
  const [isLoading, setIsLoading] = useState(false as boolean);
  const [errorMessage, setErrorMessage] = useState('' as string);

  const {
    renderModal,
    renderSaveButton,
    renderOpenButton,
    renderSnackbar
  } = useSolutionGallery();

  const {
    renderModal: renderSpecCommentsModal,
    renderOpenButton: renderSpecCommentsOpenButton
  } = useSpecComments();

  const updateActivity = (setter: (activity: Activity) => Activity) => {
    setActivity(setter(JSON.parse(JSON.stringify(activity))));
  };

  const search = async (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      setActivity(null);
      setSpec(null);
      setErrorMessage('');

      const [ course_id = '', assignment_id = '' ] = reference.split('-');
      const data = (
        await Axios.get('/admin/retrieve-submission', {
          params: { id: reference.trim() },
        })
      ).data;
      const id = (data.activityID || '') as string;
      const answer = (data.code.replace(/((\\n)+((?!\S+)|(?=})))/g, '\n') ||
        '') as string;
      setSpec(await Spec.findById(id));
      setActivity({
        id: id,
        name: id,
        answer: cleanCode(answer),
      });

    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout>
      { renderModal() }
      { renderSnackbar() }
      { renderSpecCommentsModal() }

      <div className="mt-3">
        <div className="mb-3">
          <Card>
            <CardContent>
              <Typography variant="h5" className="mb-3">Investigate Submission</Typography>
              <Typography>This tool enables administrators to access a student's submission for
              closer investigation using the reference code shown to the students after code execution.
              </Typography>
              <Typography>A sample reference code looks like this: <span>c73746c1-0773-43ff-b3ca-310006d08ce0-PI3-SPRING-E1-FRUIT</span></Typography>
            </CardContent>
          </Card>
        </div>
        <form onSubmit={search}>
          <fieldset style={{ border: 0 }} disabled={isLoading}>
            <div className="d-flex align-items-end">
              <div className="flex-fill">
                <FormControl fullWidth={true}>
                  <InputLabel>Enter Reference Code</InputLabel>
                  <Input
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    required={true}
                  />
                </FormControl>
              </div>
              <div className="ml-3">
                <Button type="submit" variant="contained" color="primary">
                  Search
                </Button>
              </div>
            </div>
          </fieldset>
        </form>

        {(() => {
          if (errorMessage)
            return (
              <div className="text-center p-5">
                <h2 className="m-0">Error Loading Activity</h2>
                <p className="m-0">{errorMessage}</p>
                <Button
                  type="button"
                  onClick={search}
                  color="primary"
                  className="mt-3"
                  variant="contained"
                >
                  Try Again
                </Button>
              </div>
            );

          if (activity && spec)
            return (
              <Box p={3}>

                <SpecTest
                  spec={spec}
                  answer={activity.answer}
                  onAnswerChange={answer => updateActivity((activity) => {
                    activity.answer = answer;
                    return activity;
                  })}
                  editorFooter={(
                    <ButtonGroup>
                      { renderSaveButton({specId: activity.id, code: activity.answer}) }
                      { renderOpenButton({specId: activity.id}) }
                      { !!spec && renderSpecCommentsOpenButton({specId: activity.id}) }
                    </ButtonGroup>
                  )}
                />
              </Box>
            );
        })()}
      </div>
    </AdminLayout>
  );
};

export default Investigate;
